import { useState } from "react";
import ModalComponent from "../../../../components/modals/ModalFormComponent";
import Label from "../../../../components/form_fields/Label";
import { SubmitHandler, useForm } from "react-hook-form";
import {
  useCreateAllowance,
  useUpdateAllowance,
} from "../../../../services/allowance.service";
import {
  ALLOWANCE_FORMULA_TYPE,
  AllowanceApplicableValuePeriod,
} from "../../../../models/allowance.dto";
import { CompanyCurrencyConfigDto } from "../../../../models/company-currency.dto";
import {
  IAddEditTableForm,
  enumToStringConverter,
  manageResourceIds,
  formatRate,
} from "../../../../helpers/appHelpers";
import useEditFormStore from "../../../../state-management/useEditFormStore";
import FormDescriptionInput from "../../../../components/form_fields/FormDescriptionInput";
import MoneyInput from "../../../../components/form_fields/MoneyInput";
import OrangeButton from "../../../../components/buttons/OrangeButton";
import { useManageTableWithSearchParamsData } from "../../../../helpers/useManageTable";
import useCompanyStore from "../../../../state-management/useCompanyStore";
import { PayPeriodDto } from "../../../../models/pay-period.dto";
import { EmployeeBandDto } from "../../../../models/employee-band.dto";
import FormCodeInput from "../../../../components/form_fields/FormCodeInput";
import MultiSelectField from "../../../../components/form_fields/MultiSelectField";
import { PATHS } from "../../../../routes/routes.paths";
import RateComponent from "../../../../components/form_fields/RateComponent";
import { EmployeeDto } from "../../../../models/employee.dto";
import APISelectField from "../../../../components/form_fields/APISelectField";
import { AllowanceGroupDto } from "../../../../models/allowance-group.dto";
import { filterPayloadData } from "../../../../services/utils/requests";

interface IFormInputs {
  code: string;
  name: string;
  description: string;
  formulaType: ALLOWANCE_FORMULA_TYPE;
  fixedComponent: number;
  rateComponent: number;
  active: string;
  groupId?: number;
  employeeIds?: number[];
  employeeBandIds?: number[];
  payPeriodIds?: number[];
  prorate: string;
  minValue: number;
  maxValue: number;
  currencyId: number;
  applicableValuePeriod: AllowanceApplicableValuePeriod;
  nonTaxable?: string;
}

type EmployeeAllowanceModalFormProps = IAddEditTableForm & {
  employees: EmployeeDto[];
  employeeBands: EmployeeBandDto[];
  payPeriods: PayPeriodDto[];
  allowanceGroups: AllowanceGroupDto[];
  companyCurrencies: CompanyCurrencyConfigDto[];
  reloadTableData?: () => void;
};

const AllowanceModalForm = ({
  open,
  setOpen,
  title,
  isEditForm,
  tableAPIRef,
  employees,
  employeeBands,
  payPeriods,
  allowanceGroups,
  companyCurrencies,
  reloadTableData,
}: EmployeeAllowanceModalFormProps) => {
  const { currentCompany } = useCompanyStore();
  const { formDataValues } = useEditFormStore();
  const isGrouped = isEditForm && !!formDataValues?.groupId;
  const [uploading, setUploading] = useState(false);

  const applicableValuePeriodToNumber: Record<string, number> = {
    MONTH: AllowanceApplicableValuePeriod.MONTH,
    YEAR: AllowanceApplicableValuePeriod.YEAR,
    QUARTER: AllowanceApplicableValuePeriod.QUARTER,
  };

  const {
    register,
    watch,
    handleSubmit,
    setValue,
    reset,
    formState: { errors },
  } = useForm<IFormInputs>({
    defaultValues: {
      name: isEditForm ? formDataValues?.name : "",
      code: isEditForm ? formDataValues?.code : "",
      description: isEditForm ? formDataValues?.description : "",
      formulaType: isEditForm ? formDataValues?.formulaType : undefined,
      fixedComponent: isEditForm ? formDataValues?.fixedComponent : "",
      rateComponent: isEditForm
        ? (() => {
            const rate = Number(formDataValues?.rateComponent ?? 0);
            // Manually created allowances store rate in decimal form (e.g. 0.102)
            // and need * 100 to display as a percentage (10.2).
            // Uploaded allowances store rate already in percentage form (e.g. 10.2)
            // so multiplying again would produce 1020 — skip the conversion.
            return (rate > 0 && rate < 1 ? rate * 100 : rate) as any;
          })()
        : "",
      groupId: isEditForm ? formDataValues?.groupId : undefined,
      active: isEditForm ? (formDataValues?.active ? "yes" : "no") : "",
      prorate: isEditForm ? (formDataValues?.prorate ? "yes" : "no") : "",
      minValue: isEditForm ? formDataValues?.minValue : undefined,
      maxValue: isEditForm ? formDataValues?.maxValue : undefined,
      currencyId: isEditForm ? formDataValues?.currencyId : undefined,
      applicableValuePeriod: isEditForm
        ? applicableValuePeriodToNumber[formDataValues?.applicableValuePeriod]
        : undefined,
      nonTaxable: isEditForm
        ? formDataValues?.nonTaxable === true
          ? "yes"
          : formDataValues?.nonTaxable === false
            ? "no"
            : undefined
        : undefined,
    },
  });

  // Seed each "current selection" state directly from formDataValues at
  // declaration time, rather than starting empty and relying on
  // MultiSelectField's `defaultValues` prop to sync it up later. That prop
  // only pre-checks the field's own display state — it never calls
  // setResourceIds itself — so if the user never touches a field on edit,
  // these would otherwise stay [] while the record already has N
  // employees/bands/pay periods attached, and manageResourceIds would read
  // that as "remove all N" on submit.
  const [employeeIds, setEmployeeIds] = useState<number[]>(() =>
    isEditForm
      ? formDataValues?.employeeAllowances?.map(
          (employeeAllowance: any) => employeeAllowance?.employee.id,
        ) || []
      : [],
  );
  const [employeeBandIds, setEmployeeBandIds] = useState<number[]>(() =>
    isEditForm
      ? formDataValues?.employeeBandAllowances?.map(
          (employeeBandAllowance: any) =>
            employeeBandAllowance?.employeeBand.id,
        ) || []
      : [],
  );
  const [payPeriodIds, setPayPeriodIds] = useState<number[]>(() =>
    isEditForm
      ? formDataValues?.payPeriods?.map((payPeriod: any) => payPeriod.id) || []
      : [],
  );

  const { mutate: createAllowance } = useCreateAllowance();
  const { mutate: updateAllowance } = useUpdateAllowance();
  const { addNewTableData } = useManageTableWithSearchParamsData();

  const onSubmit: SubmitHandler<IFormInputs> = (data: any) => {
    setUploading(true);

    formatRate(data);
    data.active = data.active === "yes" ? true : false;
    data.prorate = data.prorate === "yes" ? true : false;
    data.nonTaxable =
      data.nonTaxable === "yes"
        ? true
        : data.nonTaxable === "no"
          ? false
          : undefined;

    if (data.applicableValuePeriod !== undefined) {
      const periodMap: { [key: number]: string } = {
        [AllowanceApplicableValuePeriod.MONTH]: "MONTH",
        [AllowanceApplicableValuePeriod.QUARTER]: "QUARTER",
        [AllowanceApplicableValuePeriod.YEAR]: "YEAR",
      };
      data.applicableValuePeriod = periodMap[data.applicableValuePeriod];
    }

    if (isEditForm) {
      const { addIds: addPayPeriodIds, removeIds: removePayPeriodIds } =
        manageResourceIds({
          newData: payPeriodIds,
          oldData:
            formDataValues?.payPeriods?.map((payPeriod) => payPeriod.id) || [],
        });
      const { addIds: addEmployeeIds, removeIds: removeEmployeeIds } =
        manageResourceIds({
          newData: employeeIds,
          oldData:
            formDataValues?.employeeAllowances?.map(
              (employeeAllowance) => employeeAllowance?.employee.id,
            ) || [],
        });
      const { addIds: addEmployeeBandIds, removeIds: removeEmployeeBandIds } =
        manageResourceIds({
          newData: employeeBandIds,
          oldData:
            formDataValues?.employeeBandAllowances?.map(
              (employeeBandAllowance) => employeeBandAllowance?.employeeBand.id,
            ) || [],
        });

      data.addPayPeriodIds = addPayPeriodIds;
      data.removePayPeriodIds = removePayPeriodIds;
      data.addEmployeeIds = addEmployeeIds;
      data.removeEmployeeIds = removeEmployeeIds;
      data.addEmployeeBandIds = addEmployeeBandIds;
      data.removeEmployeeBandIds = removeEmployeeBandIds;

      delete data.currencyId;

      filterPayloadData(data);

      if (formDataValues?.groupId) {
        const allowedFields = [
          "fixedComponent",
          "rateComponent",
          "addPayPeriodIds",
          "removePayPeriodIds",
          "addEmployeeBandIds",
          "removeEmployeeBandIds",
        ];
        Object.keys(data).forEach((key) => {
          if (!allowedFields.includes(key)) {
            delete data[key];
          }
        });
      } else {
        filterPayloadData(data);
      }

      updateAllowance(
        { payload: data, id: formDataValues?.id },
        {
          onSuccess: (updatedData) => {
            try {
              tableAPIRef?.current.updateRows([updatedData.data]);
            } catch (error) {
              console.error("Error updating table rows:", error);
              reloadTableData?.();
            }
            setOpen(false);
            reset();
          },
          onSettled: () => setUploading(false),
        },
      );
    } else {
      data.employeeIds = employeeIds.length ? (employeeIds as any) : undefined;
      data.employeeBandIds = employeeBandIds.length
        ? (employeeBandIds as any)
        : undefined;
      data.payPeriodIds = payPeriodIds.length
        ? (payPeriodIds as any)
        : undefined;

      createAllowance(
        { payload: { ...data, companyId: currentCompany?.id } },
        {
          onSuccess: () => {
            addNewTableData();
            setOpen(false);
            reset();
          },
          onSettled: () => setUploading(false),
        },
      );
    }
  };

  const handleClose = () => {
    setOpen(false);
    reset();
  };

  return (
    <ModalComponent open={open} title={title} handleClose={handleClose}>
      <form onSubmit={handleSubmit(onSubmit)}>
        <div className="grid grid-cols-2 gap-x-4 gap-y-6">
          {isGrouped && (
            <p className="col-span-2 text-sm text-amber-600 bg-amber-50 rounded-lg px-4 py-2">
              Some fields are managed by the allowance group and cannot be
              edited here.
            </p>
          )}
          <div className="w-full">
            <Label title="Name" for="name" />
            <input
              defaultValue={isEditForm ? formDataValues?.name : ""}
              placeholder={"Enter name"}
              type="text"
              {...register("name", {
                required: "Enter name here",
              })}
              id="name"
              disabled={isGrouped}
              className={`w-full bg-secondary-gray rounded-lg h-14 px-5 mt-2 text-sm ${
                isGrouped ? "opacity-50 cursor-not-allowed" : ""
              } ${
                errors.name
                  ? "border border-red-500 focus:border-red-500 focus:outline-red-500"
                  : "border-none"
              }`}
            />
            {errors.name && (
              <p className="text-red-500 text-sm mt-1">{errors.name.message}</p>
            )}
          </div>
          <div className="w-full">
            <Label title="Unique Code" for="code" />
            <FormCodeInput
              register={register}
              attributes={{
                disabled: isGrouped,
                className: `w-full bg-secondary-gray rounded-lg h-14 px-5 mt-2 text-sm ${
                  isGrouped ? "opacity-50 cursor-not-allowed" : ""
                } ${
                  errors.code
                    ? "border border-red-500 focus:border-red-500 focus:outline-red-500"
                    : "border-none"
                }`,
              }}
            />
            {errors.code && (
              <p className="text-red-500 text-sm mt-1">{errors.code.message}</p>
            )}
          </div>
          <div className="w-full col-span-2">
            <Label title="Description" for="description" optional />
            <FormDescriptionInput
              required={false}
              register={register}
              attributes={{
                defaultValue: isEditForm ? formDataValues?.description : "",
                disabled: isGrouped,
                className: `w-full bg-secondary-gray rounded-lg h-14 px-5 mt-2 text-sm ${
                  isGrouped ? "opacity-50 cursor-not-allowed" : ""
                } ${
                  errors.description
                    ? "border border-red-500 focus:border-red-500 focus:outline-red-500"
                    : "border-none"
                }`,
              }}
            />
            {errors.description && (
              <p className="text-red-500 text-sm mt-1">
                {errors.description.message}
              </p>
            )}
          </div>
          <div className="w-full">
            <Label title="Formula Type" for="formulaType" />
            <select
              defaultValue={isEditForm ? formDataValues?.formulaType : ""}
              disabled={isGrouped}
              className={`w-full bg-secondary-gray rounded-lg h-14 px-5 mt-2 text-sm mb-2 ${
                isGrouped ? "opacity-50 cursor-not-allowed" : ""
              } ${
                errors.formulaType
                  ? "border border-red-500 focus:border-red-500 focus:outline-red-500"
                  : "border-none"
              }`}
              {...register("formulaType", {
                required: "Select formula type here",
              })}
            >
              <option value="">Select Formula Type</option>
              {Object.values(ALLOWANCE_FORMULA_TYPE).map((item) => (
                <option key={item} value={item}>
                  {enumToStringConverter(item)}
                </option>
              ))}
            </select>
            {errors.formulaType && (
              <p className="text-red-500 text-sm mt-1">
                {errors.formulaType.message}
              </p>
            )}
          </div>
          {isEditForm && (
            <div className="w-full">
              <Label title="Allowance Group" for="groupId" optional />
              <APISelectField
                key="groupId"
                attributes={{
                  id: "groupId",
                  defaultValue: isEditForm
                    ? formDataValues?.groupId
                    : undefined,
                  disabled: isGrouped,
                }}
                placeholder="Select Allowance Group"
                register={register}
                name="groupId"
                options={allowanceGroups}
                pathname={PATHS.SUB_COMPANY_SETTINGS.ALLOWANCE_GROUPS}
                required={false}
                error={errors.groupId}
                valueAsNumber
              />
            </div>
          )}
          <div className="w-full">
            <Label title="Fixed Component" for="fixedComponent" />
            <MoneyInput
              attributes={{
                id: "fixedComponent",
                placeholder: "0",
                disabled:
                  watch("formulaType") === ALLOWANCE_FORMULA_TYPE.CUSTOM,
              }}
              register={register}
              name="fixedComponent"
              setValue={setValue}
              defaultValue={
                isEditForm ? formDataValues?.fixedComponent : undefined
              }
              required={
                watch("formulaType") === ALLOWANCE_FORMULA_TYPE.CUSTOM
                  ? false
                  : "Enter fixed component here"
              }
              error={errors.fixedComponent}
            />
            {errors.fixedComponent && (
              <p className="text-red-500 text-sm mt-1">
                {errors.fixedComponent.message}
              </p>
            )}
          </div>
          <div className="w-full">
            <Label title="Rate Component" for="rateComponent" />
            <RateComponent
              attributes={{
                id: "rateComponent",
                disabled:
                  watch("formulaType") === ALLOWANCE_FORMULA_TYPE.CUSTOM,
              }}
              register={register}
              name="rateComponent"
              required={
                watch("formulaType") === ALLOWANCE_FORMULA_TYPE.CUSTOM
                  ? false
                  : "Enter rate component"
              }
              error={errors.rateComponent}
            />
            {errors.rateComponent && (
              <p className="text-red-500 text-sm mt-1">
                {errors.rateComponent.message}
              </p>
            )}
          </div>
          <div className="w-full">
            <Label title="Currency" for="currencyId" />
            <select
              disabled={isGrouped}
              className={`w-full bg-secondary-gray rounded-lg h-14 px-5 mt-2 text-sm mb-2 ${
                isGrouped ? "opacity-50 cursor-not-allowed" : ""
              } ${
                errors.currencyId
                  ? "border border-red-500 focus:border-red-500 focus:outline-red-500"
                  : "border-none"
              }`}
              {...register("currencyId", {
                required: "Select currency here",
                valueAsNumber: true,
              })}
            >
              <option value="">Select Currency</option>
              {companyCurrencies?.map((item) => (
                <option key={item?.id} value={item?.id}>
                  {item?.currency?.name} ({item?.currency?.code})
                </option>
              ))}
            </select>
            {errors.currencyId && (
              <p className="text-red-500 text-sm mt-1">
                {errors.currencyId.message}
              </p>
            )}
          </div>
          <div className="w-full">
            <Label
              title="Applicable Value Period"
              for="applicableValuePeriod"
            />
            <select
              defaultValue={
                isEditForm
                  ? applicableValuePeriodToNumber[
                      formDataValues?.applicableValuePeriod
                    ]
                  : ""
              }
              disabled={isGrouped}
              className={`w-full bg-secondary-gray rounded-lg h-14 px-5 mt-2 text-sm mb-2 ${
                isGrouped ? "opacity-50 cursor-not-allowed" : ""
              } ${
                errors.applicableValuePeriod
                  ? "border border-red-500 focus:border-red-500 focus:outline-red-500"
                  : "border-none"
              }`}
              {...register("applicableValuePeriod", {
                required: "Select applicable value period",
                valueAsNumber: true,
              })}
            >
              <option value="">Select Period</option>
              <option value={AllowanceApplicableValuePeriod.MONTH}>
                Month
              </option>
              <option value={AllowanceApplicableValuePeriod.QUARTER}>
                Quarter
              </option>
              <option value={AllowanceApplicableValuePeriod.YEAR}>Year</option>
            </select>
            {errors.applicableValuePeriod && (
              <p className="text-red-500 text-sm mt-1">
                {errors.applicableValuePeriod.message}
              </p>
            )}
          </div>
          <div className="w-full">
            <Label title="Minimum Value" for="minValue" />
            <MoneyInput
              attributes={{
                id: "minValue",
                placeholder: "0",
                disabled: isGrouped,
              }}
              register={register}
              name="minValue"
              setValue={setValue}
              defaultValue={isEditForm ? formDataValues?.minValue : undefined}
              required="Enter minimum value here"
              error={errors.minValue}
            />
            {errors.minValue && (
              <p className="text-red-500 text-sm mt-1">
                {errors.minValue.message}
              </p>
            )}
          </div>
          <div className="w-full">
            <Label title="Maximum Value" for="maxValue" />
            <MoneyInput
              attributes={{
                id: "maxValue",
                placeholder: "0",
                disabled: isGrouped,
              }}
              register={register}
              name="maxValue"
              setValue={setValue}
              defaultValue={isEditForm ? formDataValues?.maxValue : undefined}
              required="Enter maximum value here"
              error={errors.maxValue}
            />
            {errors.maxValue && (
              <p className="text-red-500 text-sm mt-1">
                {errors.maxValue.message}
              </p>
            )}
          </div>
          <div className="w-full">
            <Label title="Assign Pay Periods" for="payPeriodIds" optional />
            <MultiSelectField
              key={"payPeriodIds"}
              options={payPeriods}
              setResourceIds={setPayPeriodIds}
              placeholder={"Select Pay Periods"}
              pathname={PATHS.SUB_COMPANY_SETTINGS.PAY_PERIODS}
              mapText={"code"}
              required={false}
              defaultValues={
                !isEditForm
                  ? []
                  : formDataValues?.payPeriods?.map(
                      (payPeriod) => payPeriod.id,
                    ) || []
              }
            />
          </div>
          <div className="w-full">
            <Label title="Assign Employees" for="employeeIds" optional />
            <MultiSelectField
              key={"employeeIds"}
              options={employees}
              setResourceIds={setEmployeeIds}
              placeholder={"Select Employees"}
              pathname={PATHS.EMPLOYEES}
              required={false}
              mapTexts={["firstName", "lastName"]}
              disabled={isGrouped}
              defaultValues={
                !isEditForm
                  ? []
                  : formDataValues?.employeeAllowances?.map(
                      (employeeAllowance) => employeeAllowance?.employee.id,
                    ) || []
              }
            />
          </div>
          <div className="w-full">
            <Label
              title="Assign Employee Bands"
              for="employeeBandIds"
              optional
            />
            <MultiSelectField
              key={"employeeBandIds"}
              options={employeeBands}
              setResourceIds={setEmployeeBandIds}
              placeholder={"Select Bands"}
              pathname={PATHS.SUB_COMPANY_SETTINGS.BANDS}
              required={false}
              defaultValues={
                !isEditForm
                  ? []
                  : formDataValues?.employeeBandAllowances?.map(
                      (employeeBandAllowance) =>
                        employeeBandAllowance?.employeeBand.id,
                    ) || []
              }
            />
          </div>
          <div className="col-span-2">
            <div
              className={`flex space-x-12 text-base ${isGrouped ? "opacity-50 pointer-events-none" : ""}`}
            >
              <h3>Prorate</h3>
              <div className="flex space-x-3 items-center">
                <input
                  {...register("prorate", {
                    required: isGrouped ? false : "Select prorate option",
                  })}
                  defaultChecked={
                    isEditForm && formDataValues?.prorate === true
                  }
                  type="radio"
                  value={"yes"}
                  id="prorate"
                  disabled={isGrouped}
                />
                <label htmlFor="prorate">Yes</label>
              </div>
              <div className="flex space-x-3 items-center">
                <input
                  {...register("prorate", {
                    required: isGrouped ? false : "Select prorate option",
                  })}
                  defaultChecked={
                    isEditForm && formDataValues?.prorate === false
                  }
                  type="radio"
                  value={"no"}
                  id="no-prorate"
                  disabled={isGrouped}
                />
                <label htmlFor="no-prorate">No</label>
              </div>
            </div>
            {errors.prorate && (
              <p className="text-red-500 text-sm mt-1">
                {errors.prorate.message}
              </p>
            )}
          </div>
          <div className="col-span-2">
            <div
              className={`flex space-x-12 text-base ${isGrouped ? "opacity-50 pointer-events-none" : ""}`}
            >
              <h3>Active</h3>
              <div className="flex space-x-3 items-center">
                <input
                  {...register("active")}
                  defaultChecked={isEditForm && formDataValues?.active === true}
                  type="radio"
                  value={"yes"}
                  id="active"
                  disabled={isGrouped}
                />
                <label htmlFor="active">Yes</label>
              </div>
              <div className="flex space-x-3 items-center">
                <input
                  {...register("active")}
                  defaultChecked={
                    isEditForm && formDataValues?.active === false
                  }
                  type="radio"
                  value={"no"}
                  id="inactive"
                  disabled={isGrouped}
                />
                <label htmlFor="inactive">No</label>
              </div>
            </div>
            {errors.active && (
              <p className="text-red-500 text-sm mt-1">
                {errors.active.message}
              </p>
            )}
          </div>
          <div className="col-span-2">
            <div
              className={`flex space-x-12 text-base ${isGrouped ? "opacity-50 pointer-events-none" : ""}`}
            >
              <h3>Non-Taxable</h3>
              <div className="flex space-x-3 items-center">
                <input
                  {...register("nonTaxable")}
                  defaultChecked={
                    isEditForm && formDataValues?.nonTaxable === true
                  }
                  type="radio"
                  value={"yes"}
                  id="nonTaxable-yes"
                  disabled={isGrouped}
                />
                <label htmlFor="nonTaxable-yes">Yes</label>
              </div>
              <div className="flex space-x-3 items-center">
                <input
                  {...register("nonTaxable")}
                  defaultChecked={
                    isEditForm && formDataValues?.nonTaxable === false
                  }
                  type="radio"
                  value={"no"}
                  id="nonTaxable-no"
                  disabled={isGrouped}
                />
                <label htmlFor="nonTaxable-no">No</label>
              </div>
            </div>
            {errors.nonTaxable && (
              <p className="text-red-500 text-sm mt-1">
                {errors.nonTaxable.message}
              </p>
            )}
          </div>
        </div>

        <div className="w-full flex justify-end mt-8">
          <OrangeButton
            type="submit"
            title={
              uploading
                ? "Uploading..."
                : isEditForm
                  ? "Update Employee Allowance"
                  : "Add Employee Allowance"
            }
            className="bg-primary-mango text-white h-14 px-8 rounded-lg text-sm"
            disabled={uploading}
          />
        </div>
      </form>
    </ModalComponent>
  );
};

export default AllowanceModalForm;
